import { CMS_HTML_LIMITS, cmsHtmlDocCharacters, formatCmsHtmlLimit } from '@shared/cms/html-doc';
import type { SiteCmsHtmlNode, SiteCmsHtmlSource } from '@shared/types/entities';

import { htmlDocHasText, validateHtmlDoc } from '../../../../../packages/site-renderer/src/html-doc.mjs';
import { cmsIsSafeLink } from './site-cms-interaction-model';

/** Clipboard formats only. Never parsed here: the API converts, the client never sanitizes. */
export type CmsHtmlClipboard = { source: SiteCmsHtmlSource; html?: string; text?: string };

const OFFICE = /urn:schemas-microsoft-com:office|mso-|class="?Mso|ProgId content=Word/i;
export function cmsHtmlFromClipboard(data: Pick<DataTransfer, 'getData'> | null): CmsHtmlClipboard | null {
  if (!data) return null;
  const html = data.getData('text/html');
  const text = data.getData('text/plain');
  if (html.trim()) return { source: OFFICE.test(html) ? 'word' : 'browser', html };
  if (text.trim()) return { source: 'text', text };
  return null;
}

/**
 * Only the latest analysis may be applied, and only if the editor did not
 * change meanwhile: an old or ambiguous response never overwrites content.
 */
export function createCmsPasteGuard() {
  let serial = 0;
  return {
    begin(version: number) {
      const token = { serial: ++serial, version };
      return token;
    },
    accepts(token: { serial: number; version: number }, currentVersion: number): 'apply' | 'stale' | 'changed' {
      if (token.serial !== serial) return 'stale';
      return token.version === currentVersion ? 'apply' : 'changed';
    },
    cancel() {
      serial++;
    },
  };
}

const cell = (header: boolean): SiteCmsHtmlNode => ({
  type: header ? 'tableHeader' : 'tableCell',
  content: [{ type: 'paragraph' }],
});
export function cmsHtmlNewTable(rows = 3, columns = 3): SiteCmsHtmlNode {
  return {
    type: 'table',
    content: Array.from({ length: rows }, (_, row) => ({
      type: 'tableRow',
      content: Array.from({ length: columns }, () => cell(row === 0)),
    })),
  };
}

/** Rectangular grid edits on the JSON table; null means "remove the table". */
export function cmsHtmlTableEdit(
  table: SiteCmsHtmlNode,
  action: 'addRow' | 'removeRow' | 'addColumn' | 'removeColumn',
  row: number,
  column: number,
): SiteCmsHtmlNode | null | 'limit' {
  const rows = structuredClone(table.content ?? []);
  const width = rows[0]?.content?.length ?? 0;
  if (action === 'addRow') {
    if (rows.length >= CMS_HTML_LIMITS.maxRows) return 'limit';
    rows.splice(row + 1, 0, { type: 'tableRow', content: Array.from({ length: width }, () => cell(false)) });
  }
  if (action === 'removeRow') rows.splice(row, 1);
  if (action === 'addColumn') {
    if (width >= CMS_HTML_LIMITS.maxColumns) return 'limit';
    rows.forEach((r, index) =>
      r.content!.splice(column + 1, 0, cell(index === 0 && r.content!.every(c => c.type === 'tableHeader'))),
    );
  }
  if (action === 'removeColumn') rows.forEach(r => r.content!.splice(column, 1));
  if (!rows.length || !rows[0].content?.length) return null;
  return { type: 'table', content: rows };
}

export function cmsHtmlBlockSummary(title: string, doc: SiteCmsHtmlNode): string {
  if (title.trim()) return title.trim();
  const text = (function collect(node: SiteCmsHtmlNode): string {
    return (node.text ?? '') + (node.content ?? []).map(n => ` ${collect(n)}`).join('');
  })(doc)
    .replace(/\s+/g, ' ')
    .trim();
  return text ? text.slice(0, 130) : 'Section vide : collez votre contenu';
}

/** Same grammar as the publisher (shared module), checked before saving. */
export function cmsHtmlIssues(doc: SiteCmsHtmlNode): { message: string; publicationOnly?: boolean }[] {
  try {
    validateHtmlDoc(doc, href => {
      if (!cmsIsSafeLink(href)) throw new TypeError('link');
      return href;
    });
  } catch {
    return [
      {
        message:
          cmsHtmlDocCharacters(doc) > CMS_HTML_LIMITS.maxCharacters
            ? `La section HTML / Word dépasse ${formatCmsHtmlLimit(CMS_HTML_LIMITS.maxCharacters)} caractères : raccourcissez-la.`
            : 'La section HTML / Word contient une structure non prise en charge (tableau irrégulier, élément vide ou lien invalide). Annulez la dernière modification ou recollez le contenu.',
      },
    ];
  }
  return htmlDocHasText(doc)
    ? []
    : [{ publicationOnly: true, message: 'La section HTML / Word est vide : collez un contenu ou retirez-la.' }];
}
